'use client';

// Роль «Сотрудник» (docs/archive/STAFF-ROLE-PLAN.md Ф2): переключалка кабинетов, панель
// «Сотрудники» у владельца и плашка «ты в чужом кабинете». API — Ф1:
// /api/cabinet/switch (GET список, POST переключить) и /api/cabinet/employees.
// Передача кабинета — НЕ здесь: это несвязанная функция, живёт в «Профиле»
// (profile.tsx, ссылка-приглашение) — заказ владельца 2026-07-19.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { CABINET_TRANSFER_HIDDEN } from '@/lib/hidden-features';
import { useTranslations } from 'next-intl';
import { ConfirmInline } from './confirm-inline';
import { DActions, DCenter, DEmpty, DError, DFine, DGroup, DHint, DList } from './drawer-kit';
import { FBlock, FPane, KInput, KSwitch } from './form-kit';
import { KBareButton, KButton } from '@scriptera/ui/kit';
import { useFmtDate, useFmtDateTime } from './billing-shared';
import { Sk } from './skeleton';
import { usePersistentData, clearPersistentCache } from '@/lib/use-persistent-data';
import { useEditorPanel, useSlidePanel, useSlideStack } from './native-layer-host';
import type { DrawerPayload } from './drawer';
import { SettingsHero } from './settings-hero';
import { accountRoleHex, accountRowIcon } from '@/lib/account-native';
import {
  nativeBridge,
  nativeMethod,
  readRoleHex,
  type NativeAccountField,
  type NativeAccountGroup,
  type NativeAccountHero,
  type NativeAccountPage,
} from '@/lib/native-app';
import { PageAction, PageAddButton } from './page-action';

export interface CabinetEntry {
  id: string;
  email: string;
  name: string | null;
  self: boolean;
  active: boolean;
}

// Один key на все места (строка настроек, шторка, плашка) — один запрос на страницу.
export function useCabinets() {
  // SPEED §5.2: раздел смены кабинета скрыт флагом — ручку не просим (пустой адрес = без запроса).
  return usePersistentData<{ cabinets: CabinetEntry[] }>('cabinet:switch', CABINET_TRANSFER_HIDDEN ? '' : '/api/cabinet/switch', {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
  });
}

/** Переключение живёт на строке сессии (сервер), поэтому после POST — полная
    перезагрузка с зачисткой локального кэша: иначе данные прошлого кабинета
    остались бы на устройстве (тот же принцип, что при логауте). */
export async function switchCabinet(ownerId: string | null): Promise<boolean> {
  const r = await fetch('/api/cabinet/switch', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ownerId }),
  }).catch(() => null);
  if (!r?.ok) return false;
  clearPersistentCache();
  // Кабинет сменился — вместе с кэшем прошлого кабинета уходит и РОДНОЙ раздел
  // «Аккаунт»: в чужом кабинете его страницы нет вовсе (там живут одни «Настройки»),
  // некому прислать свежие данные, и без уборки экран висел бы с прежним человеком.
  // Проверка НАЛИЧИЯ метода обязательна: Plugins.* — Proxy, и вызов незарегистрированного
  // метода на старой сборке роняет страницу.
  if (nativeMethod('accountClose')) void nativeBridge()?.accountClose?.();
  window.location.assign('/app');
  return true;
}

const iconProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
export const CabinetsIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <path d="M8 7h13M8 12h13M8 17h13" />
    <path d="M3 7l1.5 1.5L7 5.5" />
  </svg>
);
export const EmployeesIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19c.6-3 2.8-4.6 5.5-4.6s4.9 1.6 5.5 4.6" />
    <path d="M15.5 5.2a3.2 3.2 0 0 1 0 5.6M17.6 14.6c1.6.7 2.6 2.2 2.9 4.4" />
  </svg>
);

function cabinetLabel(c: CabinetEntry): string {
  return c.name || (c.email.split('@')[0] ?? c.email);
}

// ─────────────────────────── Шторка: Кабинеты (переключалка) ───────────────────────────
export function CabinetSwitchPanel() {
  const t = useTranslations('cabinet.staff');
  const { data, loading } = useCabinets();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState(false);

  if (loading) return <Sk w={260} h={16} />;
  const cabinets = data?.cabinets ?? [];

  const go = async (c: CabinetEntry) => {
    if (c.active || busy) return;
    setBusy(c.id);
    setErr(false);
    const ok = await switchCabinet(c.self ? null : c.id);
    if (!ok) {
      setBusy(null);
      setErr(true);
    }
  };

  return (
    <DGroup>
      {/* Ошибка — красным сразу под шапкой (стандарт модалок, владелец 2026-07-23). */}
      {err && <DError>{t('switchError')}</DError>}
      <DHint>{t('switchIntro')}</DHint>
      <DList>
        {cabinets.map((c) => (
          <div
            key={c.id}
            className="row tap"
            style={{ cursor: c.active ? 'default' : 'pointer' }}
            onClick={() => void go(c)}
          >
            <div className="row-mid grow" style={{ minWidth: 0 }}>
              <div className="nm">
                {cabinetLabel(c)}
                {c.self ? ` — ${t('yourCabinet')}` : ''}
              </div>
              <div className="meta">{c.email}</div>
            </div>
            <div className="right">
              {c.active ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M4.5 12.5l5 5L19.5 7" />
                </svg>
              ) : busy === c.id ? (
                <span className="meta">…</span>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--mut)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M9 6l6 6-6 6" />
                </svg>
              )}
            </div>
          </div>
        ))}
      </DList>
    </DGroup>
  );
}

/** РОДНАЯ СТРАНИЦА «КАБИНЕТЫ» (docs/NATIVE-ACCOUNT-PLAN.md, этап Д). Страницу собирает
 *  ЕЁ ЖЕ рука — рядом с настройкой, а не в settings.tsx (так же сделаны «Уведомления» и
 *  «Профиль»): список берёт тот же useCabinets, переключение — тот же switchCabinet, что
 *  и веб-шторка. Второй правды о кабинетах нет нигде.
 *
 *  Текущий кабинет — ряд с ГАЛОЧКОЙ (`check`), нажимать его нечего; остальные — ряды с
 *  шевроном, тап переключает (страница после переключения перезагружается сама —
 *  switchCabinet чистит кэш и уводит на /app). */
export function useNativeCabinetsPage(
  title: string,
  meta: string,
): { page: NativeAccountPage; tapRow: (rowKey: string) => boolean } {
  const t = useTranslations('cabinet.staff');
  const { data, loading } = useCabinets();
  const [err, setErr] = useState(false);
  const cabinets = data?.cabinets ?? [];

  const page: NativeAccountPage = {
    key: 'cabinets',
    title,
    hero: { icon: accountRowIcon('cabinets'), color: accountRoleHex('var(--blue)'), title, meta },
    // Список ещё едет — пузырь стоит пустым, а не показывает один свой кабинет и не
    // достраивается на глазах (дёрганья запрещены).
    loading: loading || cabinets.length === 0,
    groups: [
      {
        key: 'cabinets',
        // Объяснение — НАД пузырём, как в вебе (DHint над списком); ошибка переключения
        // приходит под список, чтобы не сдвигать сам список при неудаче.
        label: t('switchIntro'),
        ...(err ? { hint: t('switchError') } : {}),
        fields: cabinets.map((c) => ({
          key: `cab:${c.id}`,
          kind: 'nav' as const,
          title: cabinetLabel(c) + (c.self ? ` — ${t('yourCabinet')}` : ''),
          hint: c.email,
          ...(c.active ? { check: true } : {}),
        })),
      },
    ],
  };

  const tapRow = (rowKey: string): boolean => {
    const m = /^cab:(.+)$/.exec(rowKey);
    if (!m) return false;
    const c = cabinets.find((x) => x.id === m[1]);
    if (!c || c.active) return true;
    setErr(false);
    void (async () => {
      if (!(await switchCabinet(c.self ? null : c.id))) setErr(true);
    })();
    return true;
  };

  return { page, tapRow };
}

// ─────────────────────────── Сотрудники (только владелец) ───────────────────────────
// Редизайн 2026-07-22 (заказ владельца, референс — «аккаунты» Telegram): вертикальная
// шторка со списком сотрудников (круглые аватарки-инициалы). Вложенные экраны —
// «Пригласить» (старт — галочка в шапке), «Журнал», карточка сотрудника — живут ВНУТРИ
// ТОЙ ЖЕ шторки и выезжают горизонтально справа (заказ владельца 2026-07-22: вторая
// шторка поверх первой корёжила нижнюю). Механика: ОДНА панель (useEditorPanel — в
// приложении один слой с подменой шапки на месте, в вебе drawer), тело — StaffRoot со
// своим стеком экранов и CSS-анимацией выезда; шапка (заголовок/←/галочка/журнал)
// обновляется editor.open с ТЕМ ЖЕ элементом тела — React не перемонтирует, стек жив.
interface Employee {
  id: string;
  email: string;
  name: string | null;
  /** Разделы доступа ('work'/'models', владелец 2026-07-23). */
  access: string[];
  createdAt: string;
}

/** Подпись набора разделов для списка/карточки сотрудника. */
function useAccessLabel(): (access: string[]) => string {
  const t = useTranslations('cabinet.staff');
  return (access) => {
    const work = access.includes('work');
    const models = access.includes('models');
    if (work && models) return t('accessAll');
    return work ? t('accessWorkShort') : t('accessModelsShort');
  };
}

/** `open=false` — раздел сейчас не смотрят (родная страница «Сотрудников» ещё не
 *  открыта): кэш держим, а в сеть не идём. Пустой адрес — штатный способ пропустить
 *  запрос (use-persistent-data). */
function useEmployees(open = true) {
  return usePersistentData<{ employees: Employee[] }>('cabinet:employees', open ? '/api/cabinet/employees' : '', {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
  });
}

// Приглашения этой сессии, чьи имейлы не появились в списке: сервер «ожидающих» не знает
// (анти-брут — ответ на POST всегда «ок»), поэтому строка «Приглашение отправлено» живёт
// локально (модульный стор — переживает перемонтирование панели) и исчезает, как только
// имейл стал сотрудником.
let pendingInvites: string[] = [];
const pendingSubs = new Set<() => void>();
const getPending = () => pendingInvites;
function addPending(email: string): void {
  if (pendingInvites.includes(email)) return;
  pendingInvites = [...pendingInvites, email];
  pendingSubs.forEach((fn) => fn());
}
function prunePending(existing: string[]): void {
  const next = pendingInvites.filter((e) => !existing.includes(e));
  if (next.length === pendingInvites.length) return;
  pendingInvites = next;
  pendingSubs.forEach((fn) => fn());
}
function usePendingInvites(): string[] {
  return useSyncExternalStore(
    (fn) => {
      pendingSubs.add(fn);
      return () => pendingSubs.delete(fn);
    },
    getPending,
    getPending,
  );
}

// Цвет аватарки детерминирован имейлом (как в Telegram) — у сотрудника он всюду один.
const AV_COLORS = ['var(--blue)', 'var(--violet)', 'var(--green)', 'var(--amber)', 'var(--accent)'];
/** Роль цвета аватара по имейлу. ОДНА рука на веб-кружок и на родной (волна 3):
 *  у сотрудника цвет всюду один. */
function avColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AV_COLORS[Math.abs(h) % AV_COLORS.length]!;
}
function StaffAvatar({ name, email, size = 34 }: { name: string | null; email: string; size?: number }) {
  const initial = (name?.trim()?.[0] ?? email[0] ?? '?').toUpperCase();
  return (
    <div
      className="av"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4), background: avColor(email), flex: '0 0 auto' }}
      aria-hidden
    >
      {initial}
    </div>
  );
}

const MailIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="M4.5 7.5l7.5 5.5 7.5-5.5" />
  </svg>
);
const HistoryIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" {...iconProps}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);
const Chevron = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--mut)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

// ── Экраны внутри шторки и их стек ──
// «Пригласить» (invite) экраном стека НЕ живёт (владелец 2026-07-23): он открывается
// ВЕРТИКАЛЬНОЙ шторкой поверх раздела через useSlidePanel — и в приложении, и на сайте.
type StaffScreen = { kind: 'list' } | { kind: 'journal' } | { kind: 'member'; member: Employee };
interface StaffNavApi {
  push: (s: StaffScreen) => void;
  pop: () => void;
}

/** Открывалка раздела «Сотрудники» для строки настроек. */
export function useOpenEmployees(): () => void {
  const t = useTranslations('cabinet.staff');
  const stack = useSlideStack();
  const editor = useEditorPanel();
  const panel = useSlidePanel();
  // Галочка в шапке экрана приглашения зовёт invite() тела (как ✓ кроппера аватарки).
  const inviteActRef = useRef<(() => void) | null>(null);
  const navRef = useRef<StaffNavApi | null>(null);
  const bodyRef = useRef<React.ReactElement | null>(null);
  return useMemo(() => {
    // «Пригласить» — ВЕРТИКАЛЬНОЙ шторкой поверх раздела (владелец 2026-07-23), общий
    // путь: в приложении — нижний лист стопкой, на сайте — шторка снизу поверх модалки.
    // Полная высота (без детента) — в форме поле ввода, низкий лист с клавиатурой дёргается.
    const openInvite = () =>
      panel.open({
        title: t('inviteRow'),
        sheet: true,
        body: <InviteSheet actRef={inviteActRef} onDone={() => panel.close()} />,
        actionLabel: t('inviteBtn'),
        actionIcon: 'checkmark',
        onAction: () => inviteActRef.current?.(),
      });
    // ── Приложение: НАСТОЯЩИЕ нативные слои (владелец 2026-07-22: «CSS-анимацию не
    // просил») — раздел БОКОВЫМ слоем (горизонтально, владелец 2026-07-23), вложенные
    // экраны — тоже боковыми.
    const openApp = () => {
      const pop = () => stack.pop();
      const pushJournal = () => stack.push({ title: t('logTitle'), body: <JournalSheet /> });
      const pushMember = (e: Employee) =>
        stack.push({ title: t('memberTitle'), body: <MemberSheet member={e} onDone={pop} /> });
      stack.push({
        // Страница-герой: титул между кнопками пуст, имя страницы в самом герое
        // (владелец 2026-07-24).
        title: '',
        body: (
          <>
            {/* Развёрнутое описание на ~3 строки (владелец 2026-07-24) вместо короткой
                подписи строки настроек. */}
            <SettingsHero icon={<EmployeesIcon />} title={t('employeesRow')} meta={t('employeesHeroSub')} color="var(--violet)" />
            <EmployeesPanel onInvite={openInvite} onMember={pushMember} />
          </>
        ),
        actionIcon: 'clock.arrow.circlepath',
        onAction: pushJournal,
        headerRight: (
          <KBareButton className="dr-info" onClick={pushJournal} title={t('logTitle')} aria-label={t('logTitle')}>
            <HistoryIcon />
          </KBareButton>
        ),
      });
    };
    // ── Веб (drawer, нативных слоёв нет): экраны едут внутри шторки DOM-выездом (StaffRoot).
    // Шапка под текущий экран; body — ВСЕГДА тот же элемент (не перемонтируется).
    // Раздел ГОРИЗОНТАЛЬНЫЙ (владелец 2026-07-23): sheet не ставим — на мобиле шторка
    // справа во весь экран, и вертикальный лист приглашения ложится ПОВЕРХ неё вторым слоем.
    const payloadFor = (s: StaffScreen): DrawerPayload => {
      const base = { body: bodyRef.current! };
      const back = () => navRef.current?.pop();
      switch (s.kind) {
        case 'list': {
          const openJournal = () => navRef.current?.push({ kind: 'journal' });
          return {
            ...base,
            title: t('employeesRow'),
            actionIcon: 'clock.arrow.circlepath',
            onAction: openJournal,
            headerRight: (
              <KBareButton className="dr-info" onClick={openJournal} title={t('logTitle')} aria-label={t('logTitle')}>
                <HistoryIcon />
              </KBareButton>
            ),
          };
        }
        case 'journal':
          return { ...base, title: t('logTitle'), onBack: back };
        case 'member':
          return { ...base, title: t('memberTitle'), onBack: back };
      }
    };
    const openWeb = () => {
      bodyRef.current = (
        <StaffRoot navRef={navRef} onInvite={openInvite} onNav={(s) => editor.open(payloadFor(s))} />
      );
      editor.open(payloadFor({ kind: 'list' }));
    };
    return () => (stack.available ? openApp() : openWeb());
  }, [stack, editor, panel, t]);
}

/** «Сотрудники» НА МЕСТЕ списка настроек (вторая колонка десктопа, владелец 2026-08-01):
 *  тот же список, что в шторке (герой-градиент опущен: его белая шапка живёт только на
 *  тёмном слое шторки). Стека экранов здесь нет — вложенное
 *  (сотрудник, приглашение, журнал) открывается вертикальными листами поверх страницы
 *  (канон NATIVE-SLIDE-PLAN §11 п.9), а журнал получает свою строку: шапки с кнопкой
 *  истории у страницы в развороте нет. */
/** ГЛУБОКИЕ ЭКРАНЫ «СОТРУДНИКОВ» — ОДНОЙ РУКОЙ (вертикальные листы поверх страницы,
 *  канон NATIVE-SLIDE-PLAN §11 п.9): приглашение, карточка сотрудника и журнал. Ими
 *  живут и страница в развороте, и РОДНАЯ страница раздела — второго пути к ним нет. */
function useStaffSheets(): {
  openInvite: () => void;
  openMember: (e: Employee) => void;
  openJournal: () => void;
} {
  const t = useTranslations('cabinet.staff');
  const panel = useSlidePanel();
  const inviteActRef = useRef<(() => void) | null>(null);
  const openInvite = () =>
    panel.open({
      title: t('inviteRow'),
      sheet: true,
      body: <InviteSheet actRef={inviteActRef} onDone={() => panel.close()} />,
      actionLabel: t('inviteBtn'),
      actionIcon: 'checkmark',
      onAction: () => inviteActRef.current?.(),
    });
  // Карточка сотрудника — КОНЕЧНЫЙ ОБЪЕКТ ИЗ СПИСКА, значит СТОПКА (Н39, владелец
  // 02.09.2026): лист снизу, экран под ним уходит назад колодой (§11.0).
  const openMember = (e: Employee) =>
    panel.open({
      title: t('memberTitle'),
      sheet: true,
      deck: true,
      body: <MemberSheet member={e} onDone={() => panel.close()} />,
    });
  const openJournal = () => panel.open({ title: t('logTitle'), sheet: true, body: <JournalSheet /> });
  return { openInvite, openMember, openJournal };
}

/** Ключи РОДНЫХ СТРАНИЦ «Сотрудников» (волна 3): приглашение, карточка сотрудника и
 *  журнал. Страница у ВИДА одна — карточку наполняет тот, кого выбрали. */
const STAFF_INVITE_PAGE = 'page:staff-invite';
const STAFF_MEMBER_PAGE = 'page:staff-member';
const STAFF_LOG_PAGE = 'page:staff-log';

/**
 * РОДНАЯ СТРАНИЦА «СОТРУДНИКИ» (docs/NATIVE-TABS-PLAN.md, волна 3 — аудит халявы).
 *
 * ЭТАЛОН — ВЕБ-ПАНЕЛЬ ОДИН В ОДИН (EmployeesPanel): у каждого сотрудника АВАТАР
 * (цветные инициалы, цвет считает имейл), у ожидающего приглашения — кружок с
 * КОНВЕРТОМ, «Пригласить» — круглый «+» В ШАПКЕ (как на веб-странице развороте),
 * журнал — кнопка шапки, мелкая подсказка inviteFine — ПОД списком.
 *
 * Приглашение, карточка сотрудника и журнал — РОДНЫЕ страницы стопки (прежде были
 * веб-шторками): состав тот же, пишет та же ручка кабинета.
 */
export function useNativeEmployeesPage(
  hero: NativeAccountHero,
  stack: string[],
): {
  pages: NativeAccountPage[];
  tapRow: (rowKey: string) => boolean;
  applySet: (rowKey: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('cabinet.staff');
  const accessLabel = useAccessLabel();
  const open = stack.includes('settings');
  const { data, reload } = useEmployees(open);
  const pending = usePendingInvites();
  const employees = useMemo(() => data?.employees ?? [], [data]);

  // Имейл дозрел до сотрудника — локальная строка ожидания снимается (та же уборка, что
  // у веб-панели: сервер «ожидающих» не знает).
  useEffect(() => {
    if (data) prunePending(data.employees.map((e) => e.email.toLowerCase()));
  }, [data]);

  const rows: NativeAccountField[] = [
    ...employees.map(
      (e): NativeAccountField => ({
        key: `staff:${e.id}`,
        kind: 'nav',
        page: STAFF_MEMBER_PAGE,
        title: e.name || (e.email.split('@')[0] ?? e.email),
        hint: `${e.email} · ${accessLabel(e.access ?? ['work', 'models'])}`,
        // АВАТАР сотрудника: кружок цвета, посчитанного по имейлу, и заглавная буква —
        // ровно тот же StaffAvatar, что в вебе.
        initial: (e.name?.trim()?.[0] ?? e.email[0] ?? '?').toUpperCase(),
        color: accountRoleHex(avColor(e.email)),
      }),
    ),
    // Приглашение отправлено, а человек ещё не вошёл: строка ФАКТ (нажимать нечего) с
    // серым кружком-КОНВЕРТОМ — как .av.st-pend в вебе.
    ...pending.map(
      (email): NativeAccountField => ({
        key: `pend:${email}`,
        kind: 'value',
        title: email,
        hint: t('pendingStatus'),
        initialIcon: 'envelope',
        color: readRoleHex('--line2', '#e6e6ea'),
        initialInk: readRoleHex('--mut', '#8a8a8e'),
      }),
    ),
  ];

  const page: NativeAccountPage = {
    key: 'employees',
    title: hero.title,
    hero,
    loading: !data,
    // Журнал и «Пригласить» — КНОПКАМИ ШАПКИ, как на веб-странице (владелец 2026-08-07).
    actions: [
      { key: 'staff:log', icon: 'clock.arrow.circlepath', label: t('logTitle'), page: STAFF_LOG_PAGE },
      { key: 'staff:invite', icon: 'plus', label: t('inviteRow'), page: STAFF_INVITE_PAGE },
    ],
    groups: [
      {
        key: 'staff',
        label: t('employeesHead'),
        ...(rows.length === 0 && data ? { hint: t('employeesEmpty') } : {}),
        fields: rows,
      },
      // Мелкая подсказка — ПОД списком (владелец 2026-07-24, IMG_0923).
      { key: 'staff-fine', tone: 'hint', hint: t('inviteFine'), fields: [] },
    ],
  };

  const invite = useNativeInvitePage(reload);
  const member = useNativeMemberPage(employees, reload);
  const log = useNativeLogPage(stack.includes(STAFF_LOG_PAGE));

  const tapRow = (rowKey: string): boolean => {
    if (invite.tapRow(rowKey) || member.tapRow(rowKey) || log.tapRow(rowKey)) return true;
    const m = /^staff:(.+)$/.exec(rowKey);
    if (!m) return false;
    const e = employees.find((x) => x.id === m[1]);
    // Страницу натив уже открыл сам — вебу остаётся налить в неё содержимое.
    if (e) member.show(e.id);
    return false;
  };

  const applySet = (rowKey: string, value: string | boolean): boolean => invite.applySet(rowKey, value);

  return { pages: [page, invite.page, member.page, log.page], tapRow, applySet };
}

/** РОДНАЯ СТРАНИЦА «ПРИГЛАСИТЬ» — состав веб-шторки (InviteSheet) построчно: поле
 *  имейла с пояснением, пузырь «Доступ к разделам» с двумя тумблерами и их пояснениями,
 *  ошибки словами, отправка — ✓ в шапке (в вебе она же). Пишет та же ручка кабинета. */
function useNativeInvitePage(reload: () => void): {
  page: NativeAccountPage;
  tapRow: (rowKey: string) => boolean;
  applySet: (rowKey: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('cabinet.staff');
  const [email, setEmail] = useState('');
  // Галочки разделов (владелец 2026-07-23): по умолчанию оба — полный сотрудник.
  const [accWork, setAccWork] = useState(true);
  const [accModels, setAccModels] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<'bad' | 'rate' | 'access' | 'other' | null>(null);

  const send = () => {
    if (busy) return;
    if (!/\S+@\S+\.\S+/.test(email.trim())) {
      setErr('bad');
      return;
    }
    if (!accWork && !accModels) {
      setErr('access');
      return;
    }
    setBusy(true);
    setErr(null);
    void (async () => {
      const access = [...(accWork ? ['work'] : []), ...(accModels ? ['models'] : [])];
      const r = await fetch('/api/cabinet/employees', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, access }),
      }).catch(() => null);
      setBusy(false);
      if (r?.ok) {
        // Анти-брут: сервер всегда отвечает «ок» — строка «Приглашение отправлено»
        // появляется локально, настоящим сотрудником имейл станет сам.
        addPending(email.trim().toLowerCase());
        setEmail('');
        reload();
      } else {
        setErr(r?.status === 429 ? 'rate' : 'other');
      }
    })();
  };

  const groups: NativeAccountGroup[] = [];
  if (err) {
    groups.push({
      key: 'inv-err',
      tone: 'danger',
      hint: t(
        err === 'bad'
          ? 'inviteBad'
          : err === 'rate'
            ? 'inviteRateLimited'
            : err === 'access'
              ? 'inviteAccessNone'
              : 'inviteError',
      ),
      fields: [],
    });
  }
  groups.push({
    key: 'inv-mail',
    fields: [
      {
        key: 'staff:i:email',
        kind: 'text',
        title: t('inviteLabel'),
        hint: t('inviteHint'),
        value: email,
        placeholder: t('invitePlaceholder'),
        done: t('inviteBtn'),
      },
    ],
  });
  groups.push({
    key: 'inv-access',
    label: t('inviteAccessTitle'),
    fields: [
      { key: 'staff:i:work', kind: 'switch', title: t('accessWork'), hint: t('accessWorkHint'), on: accWork },
      { key: 'staff:i:models', kind: 'switch', title: t('accessModels'), hint: t('accessModelsHint'), on: accModels },
    ],
  });
  if (busy) groups.push({ key: 'inv-busy', tone: 'hint', hint: t('inviting'), fields: [] });

  const page: NativeAccountPage = {
    key: STAFF_INVITE_PAGE,
    title: t('inviteRow'),
    hero: { icon: 'person.3', color: accountRoleHex('var(--violet)'), title: t('inviteRow') },
    loading: false,
    // Отправка — ✓ В ШАПКЕ, ровно как у веб-шторки приглашения.
    actions: [{ key: 'staff:i:go', icon: 'checkmark', label: t('inviteBtn') }],
    groups,
  };

  const tapRow = (rowKey: string): boolean => {
    if (rowKey !== 'staff:i:go') return false;
    send();
    return true;
  };

  const applySet = (rowKey: string, value: string | boolean): boolean => {
    if (rowKey === 'staff:i:email' && typeof value === 'string') {
      setEmail(value);
      setErr(null);
      return true;
    }
    if (rowKey === 'staff:i:work' && typeof value === 'boolean') {
      setAccWork(value);
      setErr(null);
      return true;
    }
    if (rowKey === 'staff:i:models' && typeof value === 'boolean') {
      setAccModels(value);
      setErr(null);
      return true;
    }
    return false;
  };

  return { page, tapRow, applySet };
}

/** РОДНАЯ КАРТОЧКА СОТРУДНИКА — состав веб-шторки (MemberSheet): крупный аватар, имя,
 *  имейл и «в команде с …», удаление с подтверждением. */
function useNativeMemberPage(
  employees: Employee[],
  reload: () => void,
): { page: NativeAccountPage; show: (id: string) => void; tapRow: (rowKey: string) => boolean } {
  const t = useTranslations('cabinet.staff');
  const fmtDate = useFmtDate();
  const [id, setId] = useState('');
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const member = employees.find((e) => e.id === id) ?? null;

  const show = (next: string) => {
    setId(next);
    setArmed(false);
    setErr(false);
  };

  const remove = () => {
    if (!member) return;
    // Подтверждение — как inline у веб-кнопки: сперва вопрос, потом снятие.
    if (!armed) {
      setArmed(true);
      return;
    }
    setBusy(true);
    setErr(false);
    void (async () => {
      const r = await fetch('/api/cabinet/employees', {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id: member.id }),
      }).catch(() => null);
      setBusy(false);
      if (r?.ok) {
        setId('');
        reload();
      } else {
        setErr(true);
      }
    })();
  };

  const groups: NativeAccountGroup[] = [];
  if (err) groups.push({ key: 'mem-err', tone: 'danger', hint: t('inviteError'), fields: [] });
  if (member) {
    groups.push({
      key: 'mem-since',
      tone: 'hint',
      hint: t('employeeSince', { date: fmtDate(member.createdAt) }),
      fields: [],
    });
    if (armed) groups.push({ key: 'mem-ask', tone: 'hint', hint: t('employeeRemoveQ'), fields: [] });
    groups.push({
      key: 'mem-act',
      fields: [{ key: 'staff:m:del', kind: 'button', title: t('memberRemove'), tone: 'danger', disabled: busy }],
    });
  }

  const page: NativeAccountPage = {
    key: STAFF_MEMBER_PAGE,
    title: t('memberTitle'),
    hero: {
      color: member ? accountRoleHex(avColor(member.email)) : accountRoleHex('var(--violet)'),
      title: member ? member.name || (member.email.split('@')[0] ?? member.email) : t('memberTitle'),
      ...(member ? { meta: member.email, initial: (member.name?.trim()?.[0] ?? member.email[0] ?? '?').toUpperCase() } : {}),
    },
    loading: !member,
    // КОНЕЧНЫЙ ОБЪЕКТ — СТОПКОЙ (Н39, словарь §11.0): карточка сотрудника — окно объекта.
    present: 'deck',
    groups,
  };

  const tapRow = (rowKey: string): boolean => {
    if (rowKey !== 'staff:m:del') return false;
    remove();
    return true;
  };

  return { page, show, tapRow };
}

/** РОДНАЯ СТРАНИЦА ЖУРНАЛА — состав веб-шторки (ActionLog): ряды с аватаром того, кто
 *  действовал, человеческой подписью действия, имейлом и временем; «Показать ещё». */
function useNativeLogPage(open: boolean): { page: NativeAccountPage; tapRow: (rowKey: string) => boolean } {
  const t = useTranslations('cabinet.staff');
  const fmtDateTime = useFmtDateTime();
  const { entries, loading, hasMore, busy, label, more } = useActionLog(open);

  const groups: NativeAccountGroup[] = [
    {
      key: 'log',
      ...(entries.length === 0 && !loading ? { hint: t('logEmpty') } : {}),
      fields: entries.map(
        (e): NativeAccountField => ({
          key: `staff:l:${e.id}`,
          kind: 'value',
          title: label(e),
          hint: `${e.actorEmail} · ${fmtDateTime(e.createdAt)}`,
          initial: (e.actorEmail[0] ?? '?').toUpperCase(),
          color: accountRoleHex(avColor(e.actorEmail)),
        }),
      ),
    },
  ];
  if (hasMore) {
    groups.push({
      key: 'log-more',
      fields: [{ key: 'staff:l:more', kind: 'button', title: busy ? '…' : t('logMore'), disabled: busy }],
    });
  }

  const page: NativeAccountPage = {
    key: STAFF_LOG_PAGE,
    title: t('logTitle'),
    hero: { icon: 'clock.arrow.circlepath', color: accountRoleHex('var(--violet)'), title: t('logTitle') },
    loading,
    groups,
  };

  const tapRow = (rowKey: string): boolean => {
    if (rowKey !== 'staff:l:more') return false;
    more();
    return true;
  };

  return { page, tapRow };
}

export function EmployeesInline() {
  const t = useTranslations('cabinet.staff');
  const { openInvite, openMember, openJournal } = useStaffSheets();
  return (
    <DGroup>
      <EmployeesPanel inline onInvite={openInvite} onMember={openMember} />
      {/* В шторке журнал открывает кнопка шапки; у страницы в развороте шапки нет —
          даём его строкой под списком. */}
      <DList>
        <div className="row tap" role="button" onClick={openJournal}>
          <div className="row-mid grow">
            <div className="nm">{t('logTitle')}</div>
          </div>
          <div className="right">
            <Chevron />
          </div>
        </div>
      </DList>
    </DGroup>
  );
}

/** Стек экранов внутри шторки с выездом справа (push) / уездом вправо (pop).
 *  Во время перехода прошлый экран живёт второй панелью и уезжает параллаксом. */
function StaffRoot({
  navRef,
  onInvite,
  onNav,
}: {
  navRef: React.MutableRefObject<StaffNavApi | null>;
  /** «Пригласить» — вертикальной шторкой поверх (владелец 2026-07-23), не экраном стека. */
  onInvite: () => void;
  onNav: (s: StaffScreen) => void;
}) {
  const t = useTranslations('cabinet.staff');
  const [stack, setStack] = useState<StaffScreen[]>([{ kind: 'list' }]);
  const [anim, setAnim] = useState<{ prev: StaffScreen; dir: 'push' | 'pop' } | null>(null);
  const cur = stack[stack.length - 1]!;

  const push = (s: StaffScreen) => {
    setAnim({ prev: cur, dir: 'push' });
    setStack((st) => [...st, s]);
    onNav(s);
  };
  const pop = () => {
    if (stack.length < 2) return;
    const next = stack[stack.length - 2]!;
    setAnim({ prev: cur, dir: 'pop' });
    setStack((st) => st.slice(0, -1));
    onNav(next);
  };
  useEffect(() => {
    navRef.current = { push, pop };
    return () => {
      navRef.current = null;
    };
  });

  const render = (s: StaffScreen) => {
    switch (s.kind) {
      case 'list':
        // Герой и на вебе тоже (владелец 2026-07-24: «в веб-версии текста нет — бред,
        // должно совпадать с приложением»).
        return (
          <>
            <SettingsHero icon={<EmployeesIcon />} title={t('employeesRow')} meta={t('employeesHeroSub')} color="var(--violet)" />
            <EmployeesPanel onInvite={onInvite} onMember={(e) => push({ kind: 'member', member: e })} />
          </>
        );
      case 'journal':
        return <JournalSheet />;
      case 'member':
        return <MemberSheet member={s.member} onDone={pop} />;
    }
  };
  const key = (s: StaffScreen) => (s.kind === 'member' ? `member:${s.member.id}` : s.kind);

  return (
    <div className="st-nav">
      {anim && (
        <div key={`prev:${key(anim.prev)}`} className={`st-pane st-prev ${anim.dir === 'push' ? 'st-out-left' : 'st-out-right'}`} aria-hidden>
          {render(anim.prev)}
        </div>
      )}
      <div
        key={key(cur)}
        className={`st-pane${anim ? (anim.dir === 'push' ? ' st-in-right' : ' st-in-left') : ''}`}
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget) setAnim(null);
        }}
      >
        {render(cur)}
      </div>
    </div>
  );
}

function EmployeesPanel({
  onInvite,
  onMember,
  inline = false,
}: {
  onInvite: () => void;
  onMember: (e: Employee) => void;
  /** Страница в правом поле: «Пригласить» уезжает В СТРОКУ ЗАГОЛОВКА (владелец
   *  2026-08-07) — широкой кнопке-градиенту на светлой странице места нет. */
  inline?: boolean;
}) {
  const t = useTranslations('cabinet.staff');
  const accessLabel = useAccessLabel();
  const { data, loading } = useEmployees();
  const pending = usePendingInvites();
  const employees = data?.employees ?? [];

  // Имейл дозрел до сотрудника (или уже был им) — локальная строка ожидания снимается.
  useEffect(() => {
    if (data) prunePending(data.employees.map((e) => e.email.toLowerCase()));
  }, [data]);

  return (
    <DGroup>
      {/* Прежний DHero-презентация убран (обход страниц 2026-07-24): дублировал
          иконку/название/описание новой шапки-героя SettingsHero прямо над ним. */}
      {/* Кнопка НАД списком (владелец 2026-07-24); список — с серым мини-оглавлением,
          пустой список не прячем («пока никого нет»). */}
      {inline ? (
        <PageAction>
          <PageAddButton label={t('inviteRow')} onClick={onInvite} />
        </PageAction>
      ) : (
        !loading && (
          <KBareButton className="k-btn--action cta-grad" onClick={onInvite}>
            {t('inviteRow')}
          </KBareButton>
        )
      )}
      {loading ? (
        <Sk w={260} h={16} />
      ) : (
        // Заголовок группы — НАД пузырём (владелец 15.08.2026), его ставит сам DList.
        <DList label={t('employeesHead')}>
          {employees.length + pending.length === 0 ? (
            <DEmpty>{t('employeesEmpty')}</DEmpty>
          ) : (
          <>
          {employees.map((e) => (
            <div key={e.id} className="row tap" role="button" onClick={() => onMember(e)}>
              <StaffAvatar name={e.name} email={e.email} />
              <div className="row-mid grow" style={{ minWidth: 0 }}>
                <div className="nm">{e.name || (e.email.split('@')[0] ?? e.email)}</div>
                <div className="meta">
                  {e.email} · {accessLabel(e.access ?? ['work', 'models'])}
                </div>
              </div>
              <div className="right">
                <Chevron />
              </div>
            </div>
          ))}
          {pending.map((email) => (
            <div key={email} className="row">
              <div className="av st-pend" style={{ width: 34, height: 34 }} aria-hidden>
                <MailIcon />
              </div>
              <div className="row-mid grow" style={{ minWidth: 0 }}>
                <div className="nm">{email}</div>
                <div className="meta">{t('pendingStatus')}</div>
              </div>
            </div>
          ))}
          </>
          )}
        </DList>
      )}
      {/* Мелкая подсказка — ПОД списком (владелец 2026-07-24, IMG_0923). */}
      <DFine>{t('inviteFine')}</DFine>
    </DGroup>
  );
}

// ─────────────────────────── Шторка: пригласить по email ───────────────────────────
function InviteSheet({
  actRef,
  onDone,
}: {
  actRef: React.MutableRefObject<(() => void) | null>;
  onDone: () => void;
}) {
  const t = useTranslations('cabinet.staff');
  const { reload } = useEmployees();
  const [email, setEmail] = useState('');
  // Галочки разделов (владелец 2026-07-23): по умолчанию оба — полный сотрудник.
  const [accWork, setAccWork] = useState(true);
  const [accModels, setAccModels] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<'bad' | 'rate' | 'access' | 'other' | null>(null);
  const valid = /\S+@\S+\.\S+/.test(email.trim());

  const invite = async () => {
    if (busy) return;
    if (!valid) {
      setErr('bad');
      return;
    }
    if (!accWork && !accModels) {
      setErr('access');
      return;
    }
    setBusy(true);
    setErr(null);
    const access = [...(accWork ? ['work'] : []), ...(accModels ? ['models'] : [])];
    const r = await fetch('/api/cabinet/employees', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, access }),
    }).catch(() => null);
    setBusy(false);
    if (r?.ok) {
      // Анти-брут: сервер всегда отвечает «ок» — в списке появляется строка «Приглашение
      // отправлено», а настоящим сотрудником имейл становится, если он зарегистрирован.
      addPending(email.trim().toLowerCase());
      reload();
      onDone();
    } else {
      setErr(r?.status === 429 ? 'rate' : 'other');
    }
  };
  // ✓ в шапке дергает свежий invite (замыкание обновляется каждый рендер, как у кроппера).
  useEffect(() => {
    actRef.current = () => void invite();
    return () => {
      actRef.current = null;
    };
  });

  return (
    <DGroup>
      {err && (
        <DError>
          {t(
            err === 'bad'
              ? 'inviteBad'
              : err === 'rate'
                ? 'inviteRateLimited'
                : err === 'access'
                  ? 'inviteAccessNone'
                  : 'inviteError',
          )}
        </DError>
      )}
      {/* Форма — набором пузырей (form-kit, FORM-DEBT): своей разметки поля и своих
          рядов-тумблеров тут больше нет. Пояснение поля — ПОД полем (канон 11.08),
          поэтому прежний вводный DHint переехал в подсказку пузыря «Email». */}
      <FPane>
        <FBlock label={t('inviteLabel')} hint={t('inviteHint')}>
          <KInput
            type="email"
            value={email}
            placeholder={t('invitePlaceholder')}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => {
              setEmail(e.target.value);
              setErr(null);
            }}
          />
        </FBlock>
        {/* Доступ к разделам — ОДНИМ пузырём с именем группы, тумблеры строками: имя
            тумблера остаётся ПРИ нём, пояснение под строкой (урок приёмки 11.08). */}
        <FBlock label={t('inviteAccessTitle')}>
          <KSwitch
            label={t('accessWork')}
            hint={t('accessWorkHint')}
            checked={accWork}
            onChange={() => {
              setAccWork(!accWork);
              setErr(null);
            }}
          />
          <KSwitch
            label={t('accessModels')}
            hint={t('accessModelsHint')}
            checked={accModels}
            onChange={() => {
              setAccModels(!accModels);
              setErr(null);
            }}
          />
        </FBlock>
      </FPane>
      {busy && <DFine>{t('inviting')}</DFine>}
    </DGroup>
  );
}

// ─────────────────────────── Низкая шторка: карточка сотрудника ───────────────────────────
function MemberSheet({ member, onDone }: { member: Employee; onDone: () => void }) {
  const t = useTranslations('cabinet.staff');
  const fmtDate = useFmtDate();
  const { reload } = useEmployees();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  const remove = async () => {
    setBusy(true);
    setErr(false);
    const r = await fetch('/api/cabinet/employees', {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: member.id }),
    }).catch(() => null);
    setBusy(false);
    if (r?.ok) {
      reload();
      onDone();
    } else {
      setErr(true);
    }
  };

  return (
    <DGroup>
      {err && <DError>{t('inviteError')}</DError>}
      <DCenter>
        <StaffAvatar name={member.name} email={member.email} size={72} />
        <b style={{ fontSize: 19 }}>{member.name || (member.email.split('@')[0] ?? member.email)}</b>
        <span className="meta">{member.email}</span>
        <span className="meta">{t('employeeSince', { date: fmtDate(member.createdAt) })}</span>
      </DCenter>
      <DActions>
        <ConfirmInline
          label={t('memberRemove')}
          question={t('employeeRemoveQ')}
          confirmLabel={t('memberRemove')}
          triggerClassName="btn danger"
          busy={busy}
          onConfirm={() => void remove()}
        />
      </DActions>
    </DGroup>
  );
}

// ─────────────────────────── Шторка: журнал действий ───────────────────────────
function JournalSheet() {
  return (
    <DGroup>
      <ActionLog />
    </DGroup>
  );
}

// ─────────────────────────── Журнал действий сотрудников (Ф3.2) ───────────────────────────
interface LogEntry {
  id: string;
  actorEmail: string;
  action: string;
  meta: Record<string, string | number | boolean | null> | null;
  createdAt: string;
}
interface LogPage {
  entries: LogEntry[];
  hasMore: boolean;
}

/** ЖУРНАЛ ДЕЙСТВИЙ — ОДНОЙ РУКОЙ: записи, догрузка и человеческие подписи. Ею живут и
 *  веб-список ниже, и РОДНАЯ страница журнала — второй правды о журнале нет.
 *  `open=false` — страницу сейчас не смотрят: кэш держим, в сеть не идём. */
function useActionLog(open = true): {
  entries: LogEntry[];
  loading: boolean;
  hasMore: boolean;
  busy: boolean;
  label: (e: LogEntry) => string;
  more: () => void;
} {
  const t = useTranslations('cabinet.staff');
  const { data, loading } = usePersistentData<LogPage>(
    'cabinet:stafflog',
    open ? '/api/cabinet/employees/log' : '',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  // Догруженные страницы («Показать ещё») живут поверх кэшированной первой.
  const [extra, setExtra] = useState<LogEntry[]>([]);
  const [tailHasMore, setTailHasMore] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  const entries = [...(data?.entries ?? []), ...extra];
  const hasMore = tailHasMore ?? data?.hasMore ?? false;

  // Человеческая строка действия; незнакомый слаг (запись новее клиента) — как есть.
  const label = (e: LogEntry) => {
    const m = e.meta ?? {};
    const name = String(m.name ?? m.title ?? m.model ?? '') || '—';
    const key =
      e.action === 'scenario.toggle'
        ? m.active
          ? 'log_scenario_on'
          : 'log_scenario_off'
        : `log_${e.action.replace(/\./g, '_')}`;
    return t.has(key) ? t(key, { name }) : e.action;
  };

  const more = () => {
    const last = entries[entries.length - 1];
    if (!last || busy) return;
    setBusy(true);
    void (async () => {
      try {
        const r = await fetch(
          `/api/cabinet/employees/log?before=${encodeURIComponent(last.createdAt)}`,
        ).catch(() => null);
        const j = (await r?.json().catch(() => null)) as ({ ok?: boolean } & LogPage) | null;
        if (j?.ok) {
          setExtra((prev) => [...prev, ...j.entries]);
          setTailHasMore(j.hasMore);
        }
      } finally {
        setBusy(false);
      }
    })();
  };

  return { entries, loading, hasMore, busy, label, more };
}

function ActionLog() {
  const t = useTranslations('cabinet.staff');
  const fmtDateTime = useFmtDateTime();
  const { entries, loading, hasMore, busy, label, more } = useActionLog();

  return (
    <>
      {loading ? (
        <Sk w={260} h={16} />
      ) : entries.length === 0 ? (
        <DHint>{t('logEmpty')}</DHint>
      ) : (
        <>
          <DList>
            {entries.map((e) => (
              <div key={e.id} className="row">
                <StaffAvatar name={null} email={e.actorEmail} />
                <div className="row-mid grow" style={{ minWidth: 0 }}>
                  <div className="nm">{label(e)}</div>
                  <div className="meta">
                    {e.actorEmail} · {fmtDateTime(e.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </DList>
          {hasMore && (
            <DActions className="st-log-more">
              <KButton size="sm" disabled={busy} onClick={more}>
                {busy ? '…' : t('logMore')}
              </KButton>
            </DActions>
          )}
        </>
      )}
    </>
  );
}

// ─────────────────────────── Плашка «ты в чужом кабинете» (шапка кабинета) ─────────────────
export function ActingBar({ cabinetName }: { cabinetName: string }) {
  const t = useTranslations('cabinet.staff');
  const [busy, setBusy] = useState(false);
  const back = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await switchCabinet(null);
    if (!ok) setBusy(false);
  };
  return (
    <div className="acting-bar" role="status">
      <span className="acting-ic" aria-hidden>
        <EmployeesIcon />
      </span>
      <span className="acting-txt">{t('actingIn', { name: cabinetName })}</span>
      <KButton size="sm" disabled={busy} onClick={() => void back()}>
        {busy ? '…' : t('actingBack')}
      </KButton>
    </div>
  );
}
